"use client"

import { usePathname } from "next/navigation"
import { useEffect } from "react"
import dynamic from "next/dynamic"
import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "@/components/ui/toaster"
import { I18nProvider } from "@/lib/i18n/context"
import { FirebaseAuthProvider } from "@/lib/firebase-context"
import { ErrorCaptureWithRoute } from "@/components/error-capture"
import { CrashLocatorOverlay } from "@/components/crash-locator-overlay"

function FallbackProviders({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

const FullProviders = dynamic(
  () =>
    import("@/app/providers")
      .then((m) => {
        const P = m?.Providers
        return { default: typeof P === "function" ? P : FallbackProviders }
      })
      .catch(() => ({ default: FallbackProviders })),
  { ssr: false },
)

/** Minimal providers for chess game route only. Avoids loading app/providers chunk that triggers TDZ. */
function ChessRouteProviders({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem
      disableTransitionOnChange
    >
      <FirebaseAuthProvider>
        <I18nProvider>
          {children}
          <Toaster />
        </I18nProvider>
      </FirebaseAuthProvider>
    </ThemeProvider>
  )
}

function isChessGameRoute(pathname: string): boolean {
  return /^\/leagues\/[^/]+\/chess\/[^/]+$/.test(pathname ?? "")
}

export function ConditionalProviders({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const isStrictRoute = pathname === "/"
  const isChess = isChessGameRoute(pathname)

  // Guardrail: on route change, reset body pointer-events (fixes Radix Dialog leaving body unclickable)
  useEffect(() => {
    const hadNone = document.body.style.pointerEvents === "none"
    document.body.style.pointerEvents = ""
    if (process.env.NODE_ENV === "development" && hadNone) {
      console.warn("[guardrail] body had pointer-events:none on route change; reset. Check for modal/overlay not cleaning up.")
    }
  }, [pathname])

  if (isStrictRoute) {
    return (
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem
        disableTransitionOnChange
      >
        <I18nProvider>
          <ErrorCaptureWithRoute />
          <CrashLocatorOverlay />
          {children}
          <Toaster />
        </I18nProvider>
      </ThemeProvider>
    )
  }

  if (isChess) {
    return (
      <ChessRouteProviders>
        <ErrorCaptureWithRoute />
        <CrashLocatorOverlay />
        {children}
      </ChessRouteProviders>
    )
  }

  return (
    <FullProviders>
      <ErrorCaptureWithRoute />
      <CrashLocatorOverlay />
      {children}
    </FullProviders>
  )
}
